import { useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import { handleInvalidRequest } from "utils/sdk";
import { addQueryParams, reverse } from "utils/urls";

import toast from "components/Toast";
import { usePageTitle } from "components/usePageTitle";

import ExpiredShareToken from "entities/Memo/components/ExpiredShareToken";
import {
  publicMemoPDFCreate,
  usePublicDealDetails,
  usePublicMemoDealDetails,
  useVerifyShareToken,
} from "entities/Memo/publicSdk";

import PrintableMemoContent from "../PrintableMemo/components/PrintableMemoContent";

import "pages/Deal/Memo/memo.css";

const PrintableShareableMemo = () => {
  const hasRun = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const params = useParams<{
    token: string;
  }>();
  const token = params.token;

  const { isValidating, error: errorToken } = useVerifyShareToken({
    token,
  });

  const { data: deal } = usePublicDealDetails({
    token: !isValidating && !errorToken ? token : undefined,
  });
  const { data: memo } = usePublicMemoDealDetails({
    token: !isValidating && !errorToken ? token : undefined,
  });

  usePageTitle(
    deal?.name && memo?.name
      ? `Builders Patch: ${deal.name} - ${memo?.name} - Memo`
      : "Builders Patch: Memo"
  );
  const navigate = useNavigate();

  const generatePDF = useCallback(async () => {
    // Let the browser render the page before generating the PDF
    await new Promise((resolve) => setTimeout(resolve, 3000));

    if (!_.isNil(token)) {
      publicMemoPDFCreate({
        token,
        html: "<!DOCTYPE html>" + document.documentElement.outerHTML,
      })
        .then((response) => {
          toast.successMessage(
            "Currently we are generating a Memo PDF file. It will be automatically downloaded when the generation has finished."
          );
          navigate(
            addQueryParams(
              reverse(URLS.SHAREABLE_MEMO, {
                token,
              }),
              { memoPDFId: response.id }
            )
          );
        })
        .catch((error) => {
          handleInvalidRequest(error);
          navigate(
            reverse(URLS.SHAREABLE_MEMO, {
              token,
            })
          );
        });
    }
  }, [token, navigate]);

  useEffect(() => {
    if (
      _.isNil(memo) ||
      hasRun.current ||
      _.isNil(deal) ||
      _.isNil(contentRef.current)
    ) {
      return;
    }

    hasRun.current = true;

    generatePDF();
  }, [token, hasRun, memo, generatePDF, deal]);

  if (errorToken) {
    return <ExpiredShareToken />;
  } else if (_.isNil(memo) || _.isNil(deal)) {
    return null;
  }
  return (
    <PrintableMemoContent
      ref={contentRef}
      deal={deal}
      memo={memo}
      token={token}
      companyLogo={memo.company_logo}
    />
  );
};

export default PrintableShareableMemo;
